import lodashGet from "lodash/get";
import lodashForOwn from "lodash/forOwn";
import dayjs from "dayjs";

import { ENDPOINT_SOCIAL, ENDPOINT_SOCIAL_MAP } from "@/common/constants/api";
import { MEDIA_TYPES } from "@/common/constants/media";
import { removeHtmlTags, getUrlDomain } from "@/common/basic.utilities";
import { encodeAllURLParameters } from "@/common/api.utilities";
import { getPrecision } from "@/common/maps/utilities";
import { addMapData, removeMapData } from "@/common/maps/features";
import { httpGet } from "@/services/api.service";
import { parseStringToObject } from "@/common/basic.utilities";
import { getTopicKey, findTopic } from "@/services/events/topics.service";

export { _getTweets as getTweets };
export { _addToMap as addToMap };
export { _removeFromMap as removeFromMap };
export { _formatFeatures as formatFeatures };
export { _formatFeature as formatFeature };

const _getTweets = async (params, types) => {
  try {
    let url;

    if (params.next) {
      url = params.next;
    } else {
      if (types) {
        params.sources = null;
        params.fields = null;
        params.timeBinSize = null;
        params.fullDoc = true;
      }

      const TYPES = types ? types.split(",") : null;

      let endpointKeys = [];
      let byTopic = "";
      if (types) {
        TYPES.forEach((item) => {
          if (item === "covid19") {
            endpointKeys.push("covid19");
          } else {
            const key = getTopicKey(item, true);
            endpointKeys.push(key);
          }
        });
        byTopic = "/topic/";
      }

      let baseEndpoint = ENDPOINT_SOCIAL + byTopic + endpointKeys.join();
      url = encodeAllURLParameters({
        baseEndpoint: baseEndpoint,
        params: params,
      });
    }

    const RESULT = await httpGet({ url: encodeURI(url) });
    const { data } = JSON.parse(RESULT);

    let tweets = data.features;

    if (!params.ignoreNext) {
      if (!params.tweets) {
        params.tweets = [];
      }
      tweets = data.features.concat(params.tweets);
    } else {
      tweets = data.features;
    }

    if (!params.ignoreNext && data.metadata && data.metadata.next) {
      params.next = data.metadata.next;
      params.tweets = tweets;
      await _getTweets(params);
    }

    _formatFeatures(tweets);

    return data;
  } catch (error) {
    Promise.reject(
      "Issue getting tweets in twitter.service.js: " + JSON.stringify(error)
    );
  }
};

const _addToMap = (params) => {
  const AGG_ENDPOINT = encodeAllURLParameters({
    baseEndpoint: ENDPOINT_SOCIAL_MAP,
    params: params,
    misc: "&precision=" + getPrecision(params.map),
  });
  const MARKER_ENDPOINT = encodeAllURLParameters({
    baseEndpoint: ENDPOINT_SOCIAL,
    params: params,
    misc: "&pageSize=10000",
  });

  addMapData({
    id: params.layerId,
    data: encodeURI(MARKER_ENDPOINT),
    aggData: encodeURI(AGG_ENDPOINT),
    map: params.map,
    mapClickEvent: params.mapClickEvent,
    clusterMapClickEvent: params.clusterMapClickEvent,
  });
};

const _removeFromMap = (params) => {
  removeMapData(params.map, params.layerId);
};

const _formatFeatures = (features) => {
  const LEN = features.length;
  for (let i = 0; i < LEN; i++) {
    let feature = features[i];
    _formatFeature(feature);
  }
};

const _formatFeature = (feature, type) => {
  if (feature.properties) {
    feature.properties.formatted = {};
    feature.properties.formatted.type = MEDIA_TYPES.twitter;
    feature.properties.formatted.adminInfo = "";

    let props = feature.properties;
    if (feature.properties.hasOwnProperty("tweet")) {
      props = feature.properties.tweet;
    }

    if (props) {
      let title = null;
      let link = null;

      if (props.screen_name && props.id_str) {
        const USER_URL = `https://twitter.com/${props.screen_name}`;

        title = `@${props.screen_name}`;
        link = `${USER_URL}/status/${props.id_str}`;
      }

      feature.properties.formatted.occurred = null;
      if (feature.properties.hasOwnProperty("@timestamp")) {
        feature.properties.formatted.occurred = dayjs(
          feature.properties["@timestamp"]
        );
      }

      feature.properties.formatted.esId = feature.properties.id_str
        ? feature.properties.id_str
        : null;

      feature.properties.formatted.title = title;
      feature.properties.formatted.link = link;
      feature.properties.formatted.source = feature.properties.formatted.link
        ? getUrlDomain(feature.properties.formatted.link)
        : null;

      feature.properties.formatted.sourceVeracity = null;
      if (lodashGet(feature, "properties.veracity")) {
        feature.properties.formatted.sourceVeracity =
          feature.properties.veracity;
      }

      feature.properties.formatted.summary = props.text
        ? removeHtmlTags(props.text)
        : null;

      feature.properties.formatted.language = props.lang ? props.lang : null;

      feature.properties.formatted.img = null;
      const MEDIA = lodashGet(props, "entities.media");
      if (MEDIA && Array.isArray(MEDIA) && MEDIA.length) {
        feature.properties.formatted.img = MEDIA[0].hasOwnProperty(
          "media_url_https"
        )
          ? MEDIA[0].media_url_https
          : null;
      }
    }

    feature.properties.formatted.location = null;
    if (
      feature.geometry &&
      feature.geometry.coordinates &&
      Array.isArray(feature.geometry.coordinates) &&
      feature.geometry.coordinates.length === 2
    ) {
      feature.properties.formatted.location = feature.geometry.coordinates;
    }

    if (lodashGet(feature, "properties.topic_scores")) {
      let topicScores = feature.properties.topic_scores;
      if (typeof topicScores === "string" || topicScores instanceof String) {
        feature.properties = parseStringToObject(
          feature.properties,
          "topic_scores"
        );
      }
      topicScores = feature.properties.topic_scores;

      // topics endpoint doesn't return the correct topic type for disasters
      if (topicScores["natural_disaster"]) {
        topicScores.disaster = topicScores["natural_disaster"];
        delete topicScores["natural_disaster"];
      }

      if (topicScores) {
        feature.properties.formatted.topic_scores =
          feature.properties.topic_scores;
        let scores = [];
        lodashForOwn(topicScores, (value, key) => {
          if (key && !isNaN(value)) {
            const TOPIC = findTopic(key, true, false);
            if (lodashGet(TOPIC, "scoreThreshold.twitter")) {
              scores.push(
                `<span class="application-heading">${key}</span>: ${(
                  value * 100
                ).toFixed(1)}% (${TOPIC.scoreThreshold.twitter * 100}%)`
              );
            }
          }
        });
        if (scores.length) {
          feature.properties.formatted.adminInfo += `${scores.join("<br/>")}`;
        }
      }
    }
  }
};
